import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Building2, Clock, CheckCircle2, XCircle, Search, MapPin, X, Loader2 } from 'lucide-react';
import { CATEGORY_META, timeAgo } from '@/lib/utils';
import { toast } from 'sonner';
import { FullPageLoader } from '@/components/Loaders';
import { AddressAutocompleteInput } from '@/components/AddressAutocompleteInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * ClaimVenuePage
 *
 * Users can find their venue (name / city / address search across 7K+ imported
 * venues) and submit a claim. If arrived via `?venue_id=...` query param
 * (e.g., from VenueDetailPage's "Claim this venue" button), the target venue
 * is pre-selected and the form is ready to submit.
 */
export default function ClaimVenuePage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedVenueId = searchParams.get('venue_id') || '';
  const { user, refresh } = useAuth();

  const [selectedVenue, setSelectedVenue] = useState(null); // full venue object
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Add-your-venue state
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addCategory, setAddCategory] = useState('bar');
  const [addAddress, setAddAddress] = useState('');
  const [addPlace, setAddPlace] = useState(null); // details from Google
  const [addPhone, setAddPhone] = useState('');
  const [adding, setAdding] = useState(false);

  // Form state
  const [fullName, setFullName] = useState(user?.display_name || '');
  const [role, setRole] = useState('Owner');
  const [phone, setPhone] = useState('');
  const [proof, setProof] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadInitial = async () => {
    try {
      // Fetch my claims + optionally pre-selected venue
      const [c, v] = await Promise.all([
        api.get('/venue-claims/me').catch(() => ({ data: [] })),
        preselectedVenueId
          ? api.get(`/venues/${preselectedVenueId}`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setClaims(c.data || []);
      if (v.data) setSelectedVenue(v.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitial();
  }, [preselectedVenueId]);

  // Debounced venue search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/venues', { params: { q: trimmed, limit: 20 } });
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const submitAddVenue = async (e) => {
    e.preventDefault();
    if (!addPlace?.lat || !addPlace?.city || !addPlace?.state) {
      toast.error('Pick the address from the suggestions so we can locate it on the map');
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post('/venues/suggest', {
        name: addName.trim(),
        category: addCategory,
        address: addPlace.address || addAddress,
        city: addPlace.city,
        state: addPlace.state,
        lat: addPlace.lat,
        lon: addPlace.lon,
        phone: addPhone.trim() || null,
      });
      toast.success('Venue added! Now submit your claim below.');
      setSelectedVenue(data);
      setAddOpen(false);
      setQuery('');
      setResults([]);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not add venue');
    } finally {
      setAdding(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedVenue?.id) {
      toast.error('Pick your venue first');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/venue-claims', {
        venue_id: selectedVenue.id,
        full_name: fullName.trim(),
        role_at_venue: role.trim(),
        contact_phone: phone.trim(),
        proof_notes: proof.trim() || null,
      });
      toast.success('Claim submitted! Admin will review shortly.');
      setPhone('');
      setProof('');
      refresh();
      // Refresh my-claims list
      const c = await api.get('/venue-claims/me').catch(() => ({ data: [] }));
      setClaims(c.data || []);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyClaimedByOther = useMemo(() => {
    if (!selectedVenue) return false;
    return Boolean(selectedVenue.owner_user_id) && selectedVenue.owner_user_id !== user?.id;
  }, [selectedVenue, user]);

  const pendingByMe = useMemo(() => {
    if (!selectedVenue) return false;
    return claims.some(
      (c) => c.venue_id === selectedVenue.id && c.status === 'pending'
    );
  }, [selectedVenue, claims]);

  if (loading) return <FullPageLoader />;

  const STATUS_BADGES = {
    pending: { label: 'Pending review', cls: 'bg-white/10 text-white/70 border-white/15', Icon: Clock },
    approved: { label: 'Approved', cls: 'bg-green-500/15 text-green-300 border-green-500/30', Icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-red-500/15 text-red-300 border-red-500/30', Icon: XCircle },
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="claim-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Claim Your Venue</h1>
          <div className="ml-auto"><Logo size={96} withText={false} /></div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <GlassCard className="p-4">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F6B24A]/15 text-[#F6B24A]">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Own or manage a venue?</p>
              <p className="text-xs text-white/70">Search for your venue below, fill in the form, and an admin will verify + approve.</p>
            </div>
          </div>
        </GlassCard>

        {/* Step 1: Pick a venue */}
        <GlassCard className="p-4 space-y-3" data-testid="claim-venue-picker-card">
          <div className="flex items-center justify-between">
            <Label className="text-white/80 text-sm">1. Which venue?</Label>
            {selectedVenue && (
              <button
                type="button"
                onClick={() => { setSelectedVenue(null); setQuery(''); setResults([]); }}
                className="text-xs text-white/60 hover:text-white underline"
                data-testid="claim-venue-change-button"
              >
                Change
              </button>
            )}
          </div>

          {selectedVenue ? (
            <VenueBadge venue={selectedVenue} />
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by venue name, city, or address&hellip;"
                  className="pl-9 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                  data-testid="claim-venue-search-input"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-white/50 hover:bg-white/10"
                    aria-label="Clear search"
                    data-testid="claim-venue-search-clear"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              {searching && (
                <div className="flex items-center gap-2 text-xs text-white/60" data-testid="claim-venue-search-loading">
                  <Loader2 className="h-3 w-3 th-spin" /> Searching&hellip;
                </div>
              )}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p className="text-sm text-white/60" data-testid="claim-venue-search-empty">
                  No venues found for &ldquo;{query.trim()}&rdquo;. Try a different search, or{' '}
                  <button
                    type="button"
                    onClick={() => { setAddName(query.trim()); setAddOpen(true); }}
                    className="text-[#F6B24A] hover:underline"
                    data-testid="claim-add-venue-open"
                  >
                    add it now
                  </button>.
                </p>
              )}
              {results.length > 0 && (
                <div className="max-h-96 space-y-1 overflow-y-auto rounded-lg border border-white/10">
                  {results.map((v) => (
                    <button
                      type="button"
                      key={v.id}
                      onClick={() => { setSelectedVenue(v); setQuery(''); setResults([]); }}
                      className="flex w-full items-center gap-3 border-b border-white/5 p-3 text-left hover:bg-white/5 last:border-b-0"
                      data-testid={`claim-venue-result-${v.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-white">{v.name}</p>
                        <p className="truncate text-xs text-white/60">
                          {CATEGORY_META[v.category]?.label || v.category} &middot; {v.city}, {v.state}
                        </p>
                      </div>
                      {v.owner_user_id ? (
                        <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/50" data-testid={`claim-venue-result-claimed-${v.id}`}>
                          Claimed
                        </span>
                      ) : v.claim_status === 'pending' ? (
                        <span className="rounded-full border border-yellow-400/30 bg-yellow-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-yellow-200">
                          Pending
                        </span>
                      ) : (
                        <span className="rounded-full border border-[#F6B24A]/40 bg-[#F6B24A]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#F6B24A]">
                          Claim
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-white/50">
                Don&apos;t see your venue?{' '}
                <button type="button" onClick={() => { setAddName(query.trim()); setAddOpen(true); }} className="text-[#F6B24A] hover:underline" data-testid="claim-add-venue-open-footer">
                  Add it now
                </button>.
              </p>

              {addOpen && (
                <form onSubmit={submitAddVenue} className="space-y-3 rounded-xl border border-[#F6B24A]/25 bg-[#F6B24A]/5 p-3" data-testid="claim-add-venue-form">
                  <p className="text-sm font-semibold text-[#F6B24A]">Add your venue</p>
                  <div>
                    <Label className="text-white/80">Venue name</Label>
                    <Input value={addName} onChange={(e) => setAddName(e.target.value)} required minLength={2}
                      className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                      placeholder="e.g., The Velvet Room" data-testid="claim-add-venue-name" />
                  </div>
                  <div>
                    <Label className="text-white/80">Category</Label>
                    <Select value={addCategory} onValueChange={setAddCategory}>
                      <SelectTrigger className="mt-1 bg-white/5 border-white/12 text-white" data-testid="claim-add-venue-category"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                        {Object.entries(CATEGORY_META).map(([slug, m]) => (
                          <SelectItem key={slug} value={slug}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/80">Street address</Label>
                    <div className="mt-1">
                      <AddressAutocompleteInput
                        value={addAddress}
                        onChange={(v) => { setAddAddress(v); setAddPlace(null); }}
                        onSelect={(d) => { setAddPlace(d); }}
                        className="bg-white/5 border-white/12 text-white placeholder:text-white/40"
                        testId="claim-add-venue-address"
                      />
                    </div>
                    {addPlace?.city && (
                      <p className="mt-1 text-xs text-emerald-300" data-testid="claim-add-venue-address-ok">
                        ✓ {addPlace.city}, {addPlace.state} {addPlace.zip || ''} — pinned on the map
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-white/80">Phone (optional)</Label>
                    <Input value={addPhone} onChange={(e) => setAddPhone(e.target.value)}
                      className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                      placeholder="(555) 555-1234" data-testid="claim-add-venue-phone" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={adding} className="flex-1 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="claim-add-venue-submit">
                      {adding ? 'Adding…' : 'Add venue'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10" data-testid="claim-add-venue-cancel">
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}

          {selectedVenue && alreadyClaimedByOther && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200" data-testid="claim-venue-already-claimed">
              This venue is already claimed by another Thirrsty user. If you believe this is an error, please contact support.
            </div>
          )}
          {selectedVenue && pendingByMe && (
            <div className="rounded-lg border border-yellow-400/30 bg-yellow-400/10 p-3 text-sm text-yellow-100" data-testid="claim-venue-pending-by-me">
              You already have a pending claim for this venue. Sit tight &mdash; an admin will review shortly.
            </div>
          )}
        </GlassCard>

        {/* Step 2: Details form (disabled until venue is picked + claimable) */}
        {selectedVenue && !alreadyClaimedByOther && !pendingByMe && (
          <form onSubmit={submit}>
            <GlassCard className="p-4 space-y-3" data-testid="claim-form-card">
              <Label className="text-white/80 text-sm">2. Your details</Label>
              <div>
                <Label className="text-white/80">Full name</Label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                  data-testid="claim-fullname-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Your role at the venue</Label>
                <Input
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  required
                  placeholder="Owner / Manager / Marketing Lead"
                  className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                  data-testid="claim-role-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Contact phone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="(555) 555-1234"
                  className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                  data-testid="claim-phone-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Proof of association (optional)</Label>
                <Textarea
                  value={proof}
                  onChange={(e) => setProof(e.target.value)}
                  placeholder="Link to website where you're listed as owner/manager, business license #, etc."
                  className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                  data-testid="claim-proof-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold disabled:opacity-60"
                data-testid="claim-submit-button"
              >
                {submitting ? 'Submitting\u2026' : 'Submit Claim'}
              </Button>
              <p className="text-center text-[11px] text-white/45">
                By submitting, you certify you have authority to represent this venue. Admin will verify + notify you.
              </p>
            </GlassCard>
          </form>
        )}

        {claims.length > 0 && (
          <section>
            <h2 className="mb-2 text-xs uppercase tracking-[0.18em] text-white/60">Your claims</h2>
            <GlassCard className="divide-y divide-white/5" data-testid="claim-history-card">
              {claims.map((c) => {
                const sb = STATUS_BADGES[c.status] || STATUS_BADGES.pending;
                const Icon = sb.Icon;
                return (
                  <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`claim-history-row-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold text-white">{c.venue_name}</p>
                      <p className="text-xs text-white/60">{c.role_at_venue} &middot; submitted {timeAgo(c.created_at)} ago</p>
                    </div>
                    <Badge className={sb.cls + ' inline-flex items-center gap-1'}>
                      <Icon className="h-3 w-3" /> {sb.label}
                    </Badge>
                  </div>
                );
              })}
            </GlassCard>
          </section>
        )}
      </main>
    </div>
  );
}

function VenueBadge({ venue }) {
  const CatIcon = MapPin;
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-[#F6B24A]/30 bg-[#F6B24A]/10 p-3"
      data-testid="claim-venue-selected-badge"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F6B24A]/25 text-[#F6B24A]">
        <CatIcon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-white">{venue.name}</p>
        <p className="truncate text-xs text-white/70">
          {CATEGORY_META[venue.category]?.label || venue.category} &middot; {venue.city}, {venue.state}
        </p>
      </div>
    </div>
  );
}
